import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useSnackBar } from './useSnackBar';
import { useTranslation } from './useTranslation';

interface CategoryPairingConfig<TStep> {
  // The step whose category rows get the name/description pairing check.
  step: TStep;
  // Form field name of the category row array (e.g. 'call_categories').
  fieldArrayName: string;
}

interface UseStepValidationGateConfig<TStep extends string, TFormValues> {
  currentStep: number;
  // Jumps directly to a step index. Callers whose own goToStep also tracks
  // a navigation direction (e.g. AgentForm's) should wrap it so this always
  // sees a single-arg (index) => void signature.
  goToStep: (index: number) => void;
  stepEnumOrder: TStep[];
  // The subset (and order) of steps applicable to the current workflow —
  // e.g. AgentForm's EDIT workflow omits the LINE step.
  applicableStepOrder: TStep[];
  methods: UseFormReturn<TFormValues & Record<string, any>>;
  workflowSidebarSteps: { title: string }[];
  firstIncompleteStep: (
    values: TFormValues,
    stepOrder: TStep[]
  ) => TStep | undefined;
  categoryPairing?: CategoryPairingConfig<TStep>;
}

// Shared Save-time full-validation gate used by both AgentForm and
// ChatAgentForm (see docs/specs/agent-form-click-to-jump-navigation.md).
// Jumps to the first incomplete step, then — once that step has actually
// mounted and registered its fields — triggers RHF validation and surfaces
// a toast naming it. Also re-runs the category name/description pairing
// check directly, since that rule isn't backed by an RHF `required`
// registration.
const useStepValidationGate = <TStep extends string, TFormValues>({
  currentStep,
  goToStep,
  stepEnumOrder,
  applicableStepOrder,
  methods,
  workflowSidebarSteps,
  firstIncompleteStep,
  categoryPairing,
}: UseStepValidationGateConfig<TStep, TFormValues>) => {
  const { t } = useTranslation();
  const displayMessage = useSnackBar();

  // Set (by the gate below) to the index of a step we've just jumped to
  // because it was found incomplete. Consumed by the effect below once
  // that step has actually mounted, so field-level errors can be
  // triggered against freshly-registered fields.
  const [pendingValidationStep, setPendingValidationStep] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (
      pendingValidationStep === null ||
      pendingValidationStep !== currentStep
    ) {
      return;
    }
    setPendingValidationStep(null);

    const stepEnum = stepEnumOrder[currentStep];
    if (categoryPairing && stepEnum === categoryPairing.step) {
      const categories =
        (methods.getValues(categoryPairing.fieldArrayName as any) as {
          name?: string;
          description?: string;
        }[]) ?? [];
      categories.forEach((cat, index) => {
        const nameEmpty = !cat.name?.trim();
        const descEmpty = !cat.description?.trim();
        if (nameEmpty && !descEmpty) {
          methods.setError(
            `${categoryPairing.fieldArrayName}.${index}.name` as any,
            { type: 'required', message: t('Category name is required') }
          );
        } else if (!nameEmpty && descEmpty) {
          methods.setError(
            `${categoryPairing.fieldArrayName}.${index}.description` as any,
            { type: 'required', message: t('Description is required') }
          );
        }
      });
    }
    methods.trigger();

    const stepTitle =
      workflowSidebarSteps[currentStep]?.title ?? t('This step');
    displayMessage(
      t('{{step}} needs a bit more information before you can save.', {
        step: stepTitle,
      }),
      'warning',
      5000
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, pendingValidationStep]);

  // Jumps to the given incomplete step and arms the effect above to
  // surface its errors/toast once it mounts. Shared by both onSubmit
  // (RHF's native validation on the current step passed) and
  // onInvalidSubmit (RHF's native validation on the current step itself
  // failed, which would otherwise short-circuit handleSubmit before our
  // own gate ever runs).
  const jumpToIncompleteStep = (incompleteStep: TStep) => {
    const incompleteIndex = stepEnumOrder.indexOf(incompleteStep);
    setPendingValidationStep(incompleteIndex);
    goToStep(incompleteIndex);
  };

  // RHF's own native `required`-field validation on the *currently
  // mounted* step runs before handleSubmit ever calls onSubmit — so if
  // the current step itself has an invalid registered field, onSubmit
  // never executes and our gate inside it would never run. This mirrors
  // the same completeness check for that case.
  const onInvalidSubmit = () => {
    const incompleteStep = firstIncompleteStep(
      methods.getValues() as TFormValues,
      applicableStepOrder
    );
    jumpToIncompleteStep(incompleteStep ?? stepEnumOrder[currentStep]);
  };

  return { jumpToIncompleteStep, onInvalidSubmit };
};

export default useStepValidationGate;
